import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPage from '../modules/auth/LoginPage';
import AdminDashboardPage from '../modules/admin/AdminDashboardPage';
import DashboardPage from '../modules/superadmin/DashboardPage';
import StaffDashboardPage from '../modules/staff/StaffDashboardPage';
import SessionExpiredModal from '../components/commoncomponents/SessionExpiredModal';
import {
  SESSION_EXPIRED_EVENTS,
} from '../services/api';

const ROUTES = {
  superadminLogin: '/superadmin/login',
  adminLogin: '/admin/login',
  staffLogin: '/login',
  legacyStaffLogin: '/staff/login',
  seaweedsStaffLogin: '/seaweeds/staff/login',
  superadminHome: '/superadmin',
  adminHome: '/admin',
  staffHome: '/staff',
};
const STAFF_PORTAL_HOSTNAME = 'seaweed.localhost';
const ADMIN_CUSTOM_ROLE_NAME = 'admin-custom';

function isAdminCustomUser(user) {
  return user?.user_type === 1 && user?.role_name === ADMIN_CUSTOM_ROLE_NAME;
}

function resolveHomeRoute(user) {
  if (isCanonicalStaffHost() && isAdminCustomUser(user)) {
    return ROUTES.staffHome;
  }

  if (user?.user_type === 0) {
    return ROUTES.superadminHome;
  }

  if (user?.user_type === 1) {
    return ROUTES.adminHome;
  }

  return ROUTES.staffHome;
}

function resolveLoginRoute(user) {
  if (isCanonicalStaffHost() && isAdminCustomUser(user)) {
    return ROUTES.staffLogin;
  }

  if (user?.user_type === 0) {
    return ROUTES.superadminLogin;
  }

  if (user?.user_type === 1) {
    return ROUTES.adminLogin;
  }

  return ROUTES.staffLogin;
}

function resolveSessionExpiredLoginRoute(user, pathname) {
  if (user) {
    return resolveLoginRoute(user);
  }

  if (pathname.startsWith(ROUTES.superadminHome) || pathname === ROUTES.superadminLogin) {
    return ROUTES.superadminLogin;
  }

  if (pathname.startsWith(ROUTES.adminHome) || pathname === ROUTES.adminLogin) {
    return ROUTES.adminLogin;
  }

  if (
    pathname.startsWith(ROUTES.staffHome) ||
    pathname === ROUTES.staffLogin ||
    pathname.startsWith(ROUTES.legacyStaffLogin) ||
    pathname.startsWith('/seaweeds/staff')
  ) {
    return ROUTES.staffLogin;
  }

  return resolveGuestLoginRoute();
}

function isCanonicalStaffHost() {
  return window.location.hostname === STAFF_PORTAL_HOSTNAME;
}

function resolveGuestLoginRoute() {
  return isCanonicalStaffHost() ? ROUTES.staffLogin : ROUTES.adminLogin;
}

function resolveFallbackRoute(session) {
  if (session) {
    return resolveHomeRoute(session.user);
  }

  return resolveGuestLoginRoute();
}

function loadStoredSession() {
  const token = sessionStorage.getItem('auth_token');
  const storedUser = sessionStorage.getItem('auth_user');

  if (!token || !storedUser) {
    return null;
  }

  try {
    return {
      token,
      user: JSON.parse(storedUser),
    };
  } catch (error) {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    return null;
  }
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(() => loadStoredSession());
  const [sessionMessage, setSessionMessage] = useState('');
  const [sessionExpiredModal, setSessionExpiredModal] = useState({
    isOpen: false,
    loginPath: '',
    message: '',
  });

  useEffect(() => {
    const isLegacySeaweedsStaffPath = location.pathname.startsWith(ROUTES.seaweedsStaffLogin);
    const isLegacyStaffLoginPath = location.pathname.startsWith(ROUTES.legacyStaffLogin);
    const isStaffHost = isCanonicalStaffHost();
    const isStaffPortalPath =
      location.pathname === ROUTES.staffLogin ||
      isLegacyStaffLoginPath ||
      location.pathname === ROUTES.staffHome ||
      isLegacySeaweedsStaffPath;
    const isLocalHostname =
      window.location.hostname === 'localhost' ||
      window.location.hostname.endsWith('.localhost');

    if (
      isStaffPortalPath &&
      isLocalHostname &&
      !isStaffHost
    ) {
      const nextUrl = new URL(window.location.href);
      nextUrl.hostname = STAFF_PORTAL_HOSTNAME;

      if (isLegacySeaweedsStaffPath || isLegacyStaffLoginPath) {
        nextUrl.pathname = ROUTES.staffLogin;
      }

      window.location.replace(nextUrl.toString());
      return;
    }

    if (location.pathname === '/') {
      navigate(resolveFallbackRoute(session), { replace: true });
    }

    if (location.search.includes('logout=1')) {
      const logoutPath =
        location.pathname === ROUTES.staffLogin
        ? ROUTES.staffLogin
        : location.pathname.startsWith(ROUTES.staffHome)
        ? ROUTES.staffLogin
        : location.pathname.startsWith('/seaweeds/staff')
          ? ROUTES.staffLogin
        : location.pathname.startsWith(ROUTES.legacyStaffLogin)
          ? ROUTES.staffLogin
        : location.pathname.startsWith(ROUTES.adminHome)
          ? ROUTES.adminLogin
          : ROUTES.superadminLogin;

      persistSession(null);
      setSession(null);
      navigate(logoutPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate, session]);

  useEffect(() => {
    function handleSessionExpired(event) {
      const loginPath = resolveSessionExpiredLoginRoute(session?.user, location.pathname);
      const message =
        event.detail?.message || 'Your session expired. Please login again.';

      persistSession(null);
      setSessionMessage(message);
      setSessionExpiredModal({
        isOpen: true,
        loginPath,
        message,
      });
    }

    SESSION_EXPIRED_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleSessionExpired);
      document.addEventListener(eventName, handleSessionExpired);
    });

    return () => {
      SESSION_EXPIRED_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleSessionExpired);
        document.removeEventListener(eventName, handleSessionExpired);
      });
    };
  }, [location.pathname, navigate, session]);

  function handleReloginAfterSessionExpired() {
    const loginPath =
      sessionExpiredModal.loginPath ||
      resolveSessionExpiredLoginRoute(session?.user, location.pathname);

    setSessionExpiredModal((currentModal) => ({
      ...currentModal,
      isOpen: false,
    }));
    setSession(null);
    navigate(loginPath, { replace: true });
  }

  function persistSession(nextSession) {
    if (!nextSession) {
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_user');
      return;
    }

    sessionStorage.setItem('auth_token', nextSession.token);
    sessionStorage.setItem('auth_user', JSON.stringify(nextSession.user));
  }

  function handleLoginSuccess({ token, user }) {
    const nextSession = { token, user };
    persistSession(nextSession);
    setSession(nextSession);
    setSessionMessage('');
    navigate(resolveHomeRoute(user), { replace: true });
  }

  function handleLogout() {
    const logoutPath = resolveLoginRoute(session?.user);
    persistSession(null);
    setSession(null);
    setSessionMessage('');
    navigate(logoutPath, { replace: true });
  }

  function handleProfileUpdate(updates) {
    setSession((currentSession) => {
      if (!currentSession) {
        return currentSession;
      }

      const nextUser = updates.user
        ? {
            ...currentSession.user,
            ...updates.user,
          }
        : {
            ...currentSession.user,
            ...updates,
          };
      const nextSession = {
        ...currentSession,
        token: updates.token || currentSession.token,
        user: nextUser,
      };

      persistSession(nextSession);
      return nextSession;
    });
  }

  function handlePasswordChanged(nextAuthState) {
    const nextSession = {
      token: nextAuthState.token,
      user: nextAuthState.user,
    };

    persistSession(nextSession);
    setSession(nextSession);
  }

  function renderLogin(portal) {
    if (portal === 'staff' && !isCanonicalStaffHost()) {
      return <Navigate to={ROUTES.adminLogin} replace />;
    }

    if (portal !== 'staff' && isCanonicalStaffHost()) {
      return <Navigate to={ROUTES.staffLogin} replace />;
    }

    if (session) {
      return <Navigate to={resolveHomeRoute(session.user)} replace />;
    }

    return (
      <LoginPage
        onLoginSuccess={handleLoginSuccess}
        portal={portal}
        sessionMessage={sessionMessage}
      />
    );
  }

  function renderDashboard(expectedUserType) {
    if (!session) {
      return (
        <Navigate
          to={
            expectedUserType === 0
              ? ROUTES.superadminLogin
              : expectedUserType === 1
                ? ROUTES.adminLogin
                : ROUTES.staffLogin
          }
          replace
        />
      );
    }

    const isAllowedAdminCustomStaffEntry =
      expectedUserType === 2 && isCanonicalStaffHost() && isAdminCustomUser(session.user);

    if (session.user.user_type !== expectedUserType && !isAllowedAdminCustomStaffEntry) {
      return <Navigate to={resolveHomeRoute(session.user)} replace />;
    }

    if (expectedUserType === 0) {
      return (
        <DashboardPage
          user={session.user}
          token={session.token}
          onLogout={handleLogout}
          onProfileUpdate={handleProfileUpdate}
          onPasswordChanged={handlePasswordChanged}
        />
      );
    }

    if (expectedUserType === 2) {
      return (
        <StaffDashboardPage
          user={session.user}
          token={session.token}
          onLogout={handleLogout}
          onProfileUpdate={handleProfileUpdate}
          onPasswordChanged={handlePasswordChanged}
        />
      );
    }

    return (
      <AdminDashboardPage
        user={session.user}
        token={session.token}
        onLogout={handleLogout}
        onProfileUpdate={handleProfileUpdate}
        onPasswordChanged={handlePasswordChanged}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to={resolveFallbackRoute(session)} replace />} />
        <Route path={ROUTES.superadminLogin} element={renderLogin('superadmin')} />
        <Route path={ROUTES.adminLogin} element={renderLogin('admin')} />
        <Route path={ROUTES.staffLogin} element={renderLogin('staff')} />
        <Route
          path={ROUTES.legacyStaffLogin}
          element={<Navigate to={ROUTES.staffLogin} replace />}
        />
        <Route
          path={ROUTES.seaweedsStaffLogin}
          element={<Navigate to={ROUTES.staffLogin} replace />}
        />
        <Route path={ROUTES.superadminHome} element={renderDashboard(0)} />
        <Route path={ROUTES.adminHome} element={renderDashboard(1)} />
        <Route path={ROUTES.staffHome} element={renderDashboard(2)} />
        <Route path="*" element={<Navigate to={resolveFallbackRoute(session)} replace />} />
      </Routes>
      <SessionExpiredModal
        isOpen={sessionExpiredModal.isOpen}
        message={sessionExpiredModal.message}
        onRelogin={handleReloginAfterSessionExpired}
      />
    </>
  );
}

function AppRoot() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <ToastContainer
        autoClose={2500}
        bodyClassName="eportal-toast__body"
        closeOnClick
        closeButton
        containerClassName="eportal-toast-container"
        draggable
        hideProgressBar={false}
        newestOnTop
        pauseOnFocusLoss={false}
        pauseOnHover
        position="bottom-right"
        progressClassName="eportal-toast__progress"
        stacked
        theme="light"
        toastClassName="eportal-toast"
      />
    </BrowserRouter>
  );
}

export default AppRoot;
